// Lib
import { convertFromRaw, EditorState } from 'draft-js';
import { cloneDeep, get, isNil } from 'lodash';

// Utils
import {
    AutoCellTypeObject,
    CellCoordsObj,
    CellData,
    CellSelections,
    CellTypeObject,
    DateTimeTypeObject,
    TableContentData,
} from '../../../../common/table/TableTypes';
import { CellTypeConstants, CellTypeNames, CellTypeObjectNames, FormatOptions } from '../cellTypes/CellTypeConstants';
import { getAllCellsBetween } from './tableCellSelectionUtils';

import {
    getDateFormattingOptions,
    getDateTimeValuesFromInputString,
    stringShouldFormatAsType,
} from './tableCellFormattingUtils';
import { getDisplayValueOfCellContentString, parseCellContentString } from './tableInitialisationUtils';
import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';
import { isFormula } from './tableFormulaUtils';
import { selectionExistsInData } from './tableDataUtils';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';

/**************************
 * GET CELL TYPE INFORMATION
 * **************************/

/**
 * This function just checks the config of the cell type to see if it allows editing, default is true
 * Most cell types are editable, but some like the checkbox are not
 */
export const typeIsReadOnly = (cellType: CellTypeObject | undefined): boolean => {
    if (!cellType || !cellType.name) return false;

    return CellTypeConstants[cellType.name]?.allowCellEditing === false;
};

export const getDefaultTypeObject = <T extends CellTypeObject>(cellTypeName: CellTypeObjectNames): T => {
    const { formattingOptions = {} } = CellTypeConstants[cellTypeName];
    const defaultOptions: { [key: string]: number | boolean | string | null } = { name: cellTypeName };

    // Loop through the formatting options and set the default value for each
    (Object.keys(formattingOptions) as Array<FormatOptions>).forEach((option) => {
        const value = formattingOptions[option]?.defaultValue;
        if (value === undefined) return;
        defaultOptions[option] = value;
    });
    // Necessary to cast to CellTypeObject object here because it will only match the type after the foreach loop
    return defaultOptions as T;
};

/**
 * Returns an object with all the shared formatting values between the selected cells
 * e.g. if all cells are currency format, object will return {name: 'currency'} but if they
 * don't share the SAME currency (like if one has 'AUD' and another has 'NZD'),
 * then that will not be added to the object, and it will still just return {name: 'currency'}
 * if they all have the accounting format, it will return {name: 'currency', accounting: true}
 */
export const getSharedFormattingValues = (
    dataArray: Array<Array<CellData>>,
    cellSelections: CellSelections,
): Partial<CellTypeObject> | AutoCellTypeObject | undefined => {
    if (!cellSelections || cellSelections.length === 0) return;

    // Check that cell selection still exists in the data array
    if (!selectionExistsInData(dataArray, cellSelections)) return;

    const selectedCellsTypes = getAllCellsBetween(cellSelections).map((cell) => dataArray[cell.row][cell.col]?.type);
    if (selectedCellsTypes.length === 1) return selectedCellsTypes[0] || { name: CellTypeNames.AUTO };

    const sharedCellFormat: { [key: string]: string | number | boolean } = {};

    // Check if all the selected cells have the same cell type name
    const names = new Set<CellTypeNames>();
    selectedCellsTypes.forEach((typeObject) => {
        names.add(typeObject?.name || CellTypeNames.AUTO);
    });
    if (names.size === 1) {
        sharedCellFormat.name = [...names][0];
    }

    // Loop through each of the format options and check if they are the same for all the selected cells
    Object.values(FormatOptions).forEach((option) => {
        const values = new Set<string | number | boolean>();
        selectedCellsTypes.forEach((typeObject) => {
            if (!typeObject) return;

            // Type assertion here is not ideal, but couldn't find a way to make it work otherwise
            // All the format options are valid keys of the CellTypeObject, but for some reason typescript doesn't recognise this
            const value = typeObject[option as unknown as keyof CellTypeObject];
            if (!isNil(value)) values.add(value);
        });

        // if there is a single value in the set, it means all the selected cells have either the same
        // value for that option, or some are undefined
        if (values.size === 1) {
            sharedCellFormat[option] = [...values][0];
        }
    });
    return sharedCellFormat;
};

export const cellCanBeDraftFormatted = (cellData: CellData): boolean => {
    return !isFormula(cellData.value) && (!cellData.type || cellData.type.name === CellTypeNames.TEXT);
};

/**************************
 * UPDATE TABLE CELL TYPE DATA
 **************************/

export const updateCellType = (
    prevCellData: CellData,
    cellTypeName: CellTypeNames,
    lastUsedCurrency: string,
): CellData => {
    const newCellData = cloneDeep(prevCellData);

    // If clicking the same type again, don't make any changes
    if (prevCellData.type?.name === cellTypeName) return newCellData;

    if (cellTypeName === CellTypeNames.AUTO) {
        // If the cell type is being changed to auto, just remove the type object
        newCellData.type = undefined;
        return newCellData;
    }

    newCellData.type = {
        ...getDefaultTypeObject(cellTypeName),
        // If the type is currency, add the last used currency to the object instead of the default value
        ...(cellTypeName === CellTypeNames.CURRENCY &&
            lastUsedCurrency && { [FormatOptions.CURRENCY]: lastUsedCurrency }),
    };

    // If the CURRENT cell type is dateTime, convert the value to raw and calculate the formatting options
    if (newCellData.type.name === CellTypeNames.DATE_TIME) {
        const rawText = rawGetText(parseCellContentString(newCellData.value || ''));
        const dateValues = getDateTimeValuesFromInputString(rawText);

        // if the current content can be converted to a date time, update the value and formatting options
        if (dateValues) {
            const { hasTime, hasSeconds } = dateValues;
            newCellData.value = rawText;

            // It's necessary to add these values to the type object here so that they are present
            // when we pass it to the getDateFormattingOptions function
            newCellData.type = { ...newCellData.type, hasTime, hasSeconds };

            // Calculate formatting options based on the new value (e.g. should show time or not)
            const format = getDateFormattingOptions(null, prevCellData.type, newCellData.value, newCellData.type);
            newCellData.type = { ...newCellData.type, ...format };
        }
    }

    // e.g. TEXT -> CURRENCY
    if (cellCanBeDraftFormatted(prevCellData) && !cellCanBeDraftFormatted(newCellData)) {
        const content = newCellData.value && parseCellContentString(newCellData.value);

        if (content && typeof content === 'object') {
            newCellData.value = rawGetText(content);
            newCellData.textStyle = EditorState.createWithContent(convertFromRaw(content))
                .getCurrentInlineStyle()
                .toArray();
        }
    }

    return newCellData;
};

/**
 * Returns a boolean value indicating whether the cell type should be updated based on new input
 * Returns true if current type is auto, or new types are currency or percentage
 * or if the current type is DateTime and the new type is Number
 * And the new type is not the same as the current type
 */
export const shouldUpdateCellType = (
    existingTypeName: CellTypeNames | undefined,
    newTypeName: CellTypeNames,
): boolean =>
    existingTypeName !== newTypeName &&
    (!existingTypeName ||
        newTypeName === CellTypeNames.PERCENTAGE ||
        newTypeName === CellTypeNames.CURRENCY ||
        newTypeName === CellTypeNames.DATE_TIME ||
        (existingTypeName === CellTypeNames.DATE_TIME && newTypeName === CellTypeNames.NUMBER));

/**
 * If the user enters some content related to a certain cell type (such as
 * a currency symbol in front of a number), update the cell type to match and return the number value
 */
export const formatCellAsType = (cellData: CellData, oldValue: string | null = null): CellData => {
    const { type: existingType, value = '' } = cellData;
    const { name: existingTypeName } = existingType || {};
    const { NUMBER, CURRENCY, PERCENTAGE, TEXT, CHECKBOX } = CellTypeNames;

    // Don't format these types
    if (existingTypeName === TEXT || existingTypeName === CHECKBOX) return cellData;

    const cellValue = value || '';

    const rawText = getDisplayValueOfCellContentString(cellValue);

    // Return if empty or formula result
    if (!rawText || typeof rawText === 'number') return cellData;

    const formatType = stringShouldFormatAsType(rawText.toString());
    if (!formatType) return cellData;

    // **************************************************
    // Determine whether to update just the value, or both type and value

    const { newCellType, newValue, otherTypeOptions } = formatType;

    // Update cell type if current type is auto, or new types are currency or percentage
    // or if the current type is DateTime and the new type is Number
    const shouldUpdateType = shouldUpdateCellType(existingTypeName, newCellType);

    // Update cell value if we are updating the type, new type matches current, or
    // current type is currency/percentage and the new type is number
    const shouldUpdateValue =
        shouldUpdateType ||
        existingTypeName === newCellType ||
        ((existingTypeName === CURRENCY || existingTypeName === PERCENTAGE) && newCellType === NUMBER);

    // If neither value nor type&value update are allowed, don't update anything
    if (!shouldUpdateValue && !shouldUpdateType) return cellData;

    // **************************************************
    // Apply the new cell, type and formatting values

    const newCellData = cloneDeep(cellData);

    if (shouldUpdateType) newCellData.type = getDefaultTypeObject(newCellType);

    // add any newly calculated type options to the type object, even if the type wasn't updated
    // this is because it's most likely formatting options based on the new input
    newCellData.type = { ...newCellData.type, ...otherTypeOptions } as CellTypeObject;

    // Update the value, either in draftjs format or as a string
    newCellData.value = cellCanBeDraftFormatted(newCellData)
        ? cellValue.replace(rawText, newValue.toString())
        : newValue.toString();

    // Set some dateTime specific format options
    if (newCellData.type?.name === CellTypeNames.DATE_TIME) {
        newCellData.type = {
            ...newCellData.type,
            ...getDateFormattingOptions(oldValue, existingType, newValue, newCellData.type as DateTimeTypeObject),
        } as DateTimeTypeObject;
    }

    return newCellData;
};

const CellTypePriority = {
    [CellTypeNames.CHECKBOX]: 0,
    [CellTypeNames.DATE_TIME]: 1,
    [CellTypeNames.TEXT]: 2,
    [CellTypeNames.CURRENCY]: 3,
    [CellTypeNames.NUMBER]: 4,
    [CellTypeNames.PERCENTAGE]: 5,
};

/**
 * Compare two cell types and returns:
 * - 1 if the first type has a higher priority than the second
 * - -1 if the first type has a less priority than the second
 * - 0 if they have the same priority
 */
export const compareCellTypesPriority = (
    type1: CellTypeObject | undefined,
    type2: CellTypeObject | undefined,
): number => {
    if (!type1) return -1;
    if (!type2) return 1;

    if (CellTypePriority[type1.name] === CellTypePriority[type2.name]) {
        const type1DecimalPlaces =
            (FormatOptions.DECIMALS in type1 && parseInt(type1[FormatOptions.DECIMALS] as string)) || -1;
        const type2DecimalPlaces =
            (FormatOptions.DECIMALS in type2 && parseInt(type2[FormatOptions.DECIMALS] as string)) || -1;

        return type1DecimalPlaces > type2DecimalPlaces ? 1 : -1;
    }

    return CellTypePriority[type1.name] < CellTypePriority[type2.name] ? 1 : -1;
};

export const inferFormulaCellType = (
    elementId: string,
    tableContentData: TableContentData,
    { row, col }: CellCoordsObj,
): CellTypeObject | undefined => {
    const sheet = hyperFormulaInstance.getSheetId(elementId);
    if (sheet === undefined) return;

    const { value } = get(tableContentData, [row, col]) || {};
    if (!isFormula(value)) return;

    /************************************************
     *  Infer formula cell type based on the cell type of the cells referenced by the formula
     */

    const inferredType = hyperFormulaInstance
        .getCellPrecedents({ sheet, row, col })
        .reduce<CellTypeObject | undefined>((selectedType, cellPrecedent) => {
            const isCellAddress = 'row' in cellPrecedent && 'col' in cellPrecedent;
            if (isCellAddress) {
                const { type } = get(tableContentData, [cellPrecedent.row, cellPrecedent.col]);
                if (compareCellTypesPriority(type, selectedType) > 0) selectedType = type;
            }

            const isCellRange = 'start' in cellPrecedent && 'end' in cellPrecedent;
            if (isCellRange) {
                const { start, end } = cellPrecedent;

                getAllCellsBetween([[start.row, start.col, end.row, end.col]]).forEach(({ row, col }) => {
                    const { type } = get(tableContentData, [row, col]);
                    if (compareCellTypesPriority(type, selectedType) > 0) selectedType = type;
                });
            }

            return selectedType;
        }, undefined);

    if (inferredType) return inferredType;

    /************************************************
     *  Infer formula cell type based on HyperFormula's deduced type
     */

    const hfType = hyperFormulaInstance.getCellValueDetailedType({ sheet, row, col });

    if (hfType === 'NUMBER_DATE') return getDefaultTypeObject(CellTypeNames.DATE_TIME);
    if (hfType === 'NUMBER_DATETIME')
        return {
            ...getDefaultTypeObject<DateTimeTypeObject>(CellTypeNames.DATE_TIME),
            [FormatOptions.HAS_TIME]: true,
        };
};
